import { Injectable, NgZone, inject, computed, effect } from "@angular/core";
import {
  Auth,
  User,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  authState,
} from "@angular/fire/auth";
import { Router } from "@angular/router";
import { Observable } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { ToastService } from "./toast.service";

/**
 * Milliseconds of session left, given the last activity stamp. Zero once idle
 * for `duration` or when there is no stamp at all (another tab signed out and
 * cleared it). Pure so the timer arithmetic has a spec.
 */
export function remainingSession(
  lastActivity: number | null,
  now: number,
  duration: number,
): number {
  if (lastActivity == null) return 0;
  return Math.max(0, duration - (now - lastActivity));
}

interface FirebaseAuthError extends Error {
  code: string;
  message: string;
}

function isFirebaseAuthError(error: unknown): error is FirebaseAuthError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    typeof (error as FirebaseAuthError).code === 'string'
  );
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private auth = inject(Auth);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private toast = inject(ToastService);

  user = toSignal(authState(this.auth), { initialValue: null });
  isAuthenticated = computed(() => this.user() !== null);
  
  user$: Observable<User | null> = authState(this.auth);
  
  private sessionTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly SESSION_DURATION = 60 * 60 * 1000;
  private readonly LAST_ACTIVITY_KEY = 'taolo_session_last_activity';
  /** Activity writes the stamp at most this often — it is a localStorage write per tap otherwise. */
  private readonly ACTIVITY_THROTTLE = 60 * 1000;

  constructor() {
    this.initSessionManagement();
    this.listenForActivity();
  }

  private getStoredLastActivity(): number | null {
    const raw = localStorage.getItem(this.LAST_ACTIVITY_KEY);
    if (raw == null) return null;
    const t = Number(raw);
    return Number.isFinite(t) ? t : null;
  }

  private setStoredLastActivity(ms: number): void {
    localStorage.setItem(this.LAST_ACTIVITY_KEY, String(ms));
  }

  private clearStoredLastActivity(): void {
    localStorage.removeItem(this.LAST_ACTIVITY_KEY);
  }

  private initSessionManagement(): void {
    effect(() => {
      const user = this.user();
      this.clearSessionTimeout();
      if (!user) {
        this.clearStoredLastActivity();
        return;
      }
      const now = Date.now();
      let lastActivity = this.getStoredLastActivity();
      if (lastActivity == null) {
        lastActivity = now;
        this.setStoredLastActivity(lastActivity);
      }
      const remaining = remainingSession(lastActivity, now, this.SESSION_DURATION);
      if (remaining === 0) {
        this.expireSession();
        return;
      }
      this.startSessionTimeout(remaining);
    });
  }

  /**
   * The timeout is IDLE, not absolute: any tap or keypress pushes the deadline
   * out by the full duration. The stamp lives in localStorage so a second tab's
   * activity counts too — the timer re-reads it before signing anyone out.
   *
   * Registered outside Angular and never writing a signal, so a pointerdown
   * does not schedule change detection on every screen.
   */
  private listenForActivity(): void {
    this.ngZone.runOutsideAngular(() => {
      const touch = () => this.touch();
      const opts: AddEventListenerOptions = { capture: true, passive: true };
      document.addEventListener("pointerdown", touch, opts);
      document.addEventListener("keydown", touch, opts);
    });
  }

  private touch(): void {
    if (!this.user()) return;
    const now = Date.now();
    const last = this.getStoredLastActivity();
    if (last != null && now - last < this.ACTIVITY_THROTTLE) return;
    this.setStoredLastActivity(now);
    this.clearSessionTimeout();
    this.startSessionTimeout(this.SESSION_DURATION);
  }

  private expireSession(): void {
    this.clearStoredLastActivity();
    // signOut rethrows; unawaited here so the toast still shows.
    void this.signOut().catch((err: unknown) =>
      console.error("Sign-out after session expiry failed:", err),
    );
    this.toast.error("Your session has expired. Please sign in again.");
  }

  private startSessionTimeout(ms: number): void {
    this.ngZone.runOutsideAngular(() => {
      this.sessionTimeout = setTimeout(() => {
        // Another tab may have been active since this timer was armed; its
        // stamp is the shared truth, so re-read it rather than trust `ms`.
        const remaining = remainingSession(
          this.getStoredLastActivity(),
          Date.now(),
          this.SESSION_DURATION,
        );
        if (remaining > 0) {
          this.startSessionTimeout(remaining);
          return;
        }
        this.ngZone.run(() => this.expireSession());
      }, ms);
    });
  }

  private clearSessionTimeout(): void {
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
      this.sessionTimeout = null;
    }
  }

  async signIn(email: string, password: string): Promise<void> {
    try {
      await signInWithEmailAndPassword(this.auth, email, password);
      await this.router.navigate(["/"]);
    } catch (error: unknown) {
      if (isFirebaseAuthError(error)) {
        console.error('Sign-in failed:', error.code, error.message);
      } else {
        console.error('Sign-in failed with unexpected error:', error);
      }
      throw error;
    }
  }

  async signOut(): Promise<void> {
    try {
      this.clearSessionTimeout();
      await signOut(this.auth);
      await this.router.navigate(["/sign-in"]);
    } catch (error: unknown) {
      if (isFirebaseAuthError(error)) {
        console.error('Sign-out failed:', error.code, error.message);
      } else {
        console.error('Sign-out failed with unexpected error:', error);
      }
      throw error;
    }
  }

  async resetPassword(email: string): Promise<void> {
    try {
      const actionCodeSettings = {
        url: window.location.origin + '/set-password',
        handleCodeInApp: true,
      };
      await sendPasswordResetEmail(this.auth, email, actionCodeSettings);
    } catch (error: unknown) {
      if (isFirebaseAuthError(error)) {
        console.error('Password reset failed:', error.code, error.message);
      } else {
        console.error('Password reset failed with unexpected error:', error);
      }
      throw error;
    }
  }

  async sendSignInLink(email: string): Promise<void> {
    try {
      const actionCodeSettings = {
        url: window.location.origin,
        handleCodeInApp: true,
      };
      await sendSignInLinkToEmail(this.auth, email, actionCodeSettings);
      window.localStorage.setItem("emailForSignIn", email);
    } catch (error: unknown) {
      if (isFirebaseAuthError(error)) {
        console.error('Send sign-in link failed:', error.code, error.message);
      } else {
        console.error('Send sign-in link failed with unexpected error:', error);
      }
      throw error;
    }
  }

  async isSignInLink(url: string): Promise<boolean> {
    return isSignInWithEmailLink(this.auth, url);
  }

  /**
   * Current user's Firebase ID token, for the `Authorization` header on Task API
   * calls. Null when signed out.
   *
   * The SDK caches the token and refreshes it automatically when it is close to
   * expiring, so calling this per request is cheap and always yields a valid
   * token. Do not cache the result — the API verifies with `checkRevoked`, so a
   * stale token from a revoked session would be rejected.
   */
  async getIdToken(): Promise<string | null> {
    const user = this.user();
    if (!user) return null;
    return user.getIdToken();
  }

  async signInWithEmailLink(email?: string): Promise<void> {
    try {
      const emailToUse =
        email || window.localStorage.getItem("emailForSignIn") || "";
      if (!emailToUse) {
        throw new Error("Email is required for sign in");
      }
      await signInWithEmailLink(this.auth, emailToUse, window.location.href);
      window.localStorage.removeItem("emailForSignIn");
      await this.router.navigate(["/"]);
    } catch (error: unknown) {
      if (isFirebaseAuthError(error)) {
        console.error('Sign-in with email link failed:', error.code, error.message);
      } else if (error instanceof Error) {
        console.error('Sign-in with email link failed:', error.message);
      } else {
        console.error('Sign-in with email link failed with unexpected error:', error);
      }
      throw error;
    }
  }
}
